import { fn } from '@ember/helper';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import { not } from 'tio-ui/utilities';
import * as v from 'valibot';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedObject } from '@ember/reactive/collections';
import CardsGeneric from '../../../tuition-assistance/cards/generic.gts';
import Component from '@glimmer/component';
import { divide } from 'tio-ui/math';
import EditScholarships from './edit-scholarships.gts';
import { Textarea } from 'tio-ui/components/forms';
import safeFormatNumber from '../../../../helpers/safe-format-number.ts';
import type { ReceivedScholarship } from '../../../../types/tuition-assistance.ts';
import type { TASApplicationModelFieldsSignature } from '../../../../models/tas-application.ts';
import type TasApplication from '../../../../models/tas-application.ts';
import type Owner from '@ember/owner';
import Button from 'tio-reimagined/components/button';
import { on } from '@ember/modifier';

type Scholarship = {
  scholarshipAmount: number;
  scholarshipName: string;
};

interface TuitionAssistanceProgramDetailsAppDetailsScholarshipCardSignature {
  Args: {
    application: TasApplication;
    isEditable: boolean;
    isAdminView?: boolean;
    onUpdateScholarships?: (arg0: ReceivedScholarship[], arg1?: string) => void;
  };
}

const changelogSchema = v.pipe(v.string(), v.nonEmpty('This field is required'));

export default class TuitionAssistanceProgramDetailsAppDetailsScholarshipCardComponent extends Component<TuitionAssistanceProgramDetailsAppDetailsScholarshipCardSignature> {
  @tracked isEditing = false;
  @tracked editableScholarships: Scholarship[] = [];
  @tracked changelog = '';
  @tracked hasSubmitted = false;

  applicationFieldsCopy!: TASApplicationModelFieldsSignature;

  constructor(
    owner: Owner,
    args: TuitionAssistanceProgramDetailsAppDetailsScholarshipCardSignature['Args']
  ) {
    super(owner, args);
    this.applicationFieldsCopy = trackedObject(args.application.fields);
    this.setOriginalValuesFromArgs();
  }

  get scholarshipCardVisible() {
    return this.args.application.tasProgramInstance.tasProgramTemplate.scholarshipsAreVisible;
  }

  get changelogValidationErrors() {
    const result = v.safeParse(changelogSchema, this.changelog);
    if (!result.success) {
      return result.issues.map((issue) => issue.message);
    }
    return [];
  }

  get allScholarshipsAreValid() {
    return this.editableScholarships.every((scholarship) => {
      return scholarship.scholarshipName && scholarship.scholarshipAmount;
    });
  }

  get isFormValid() {
    if (this.args.isAdminView) {
      return !this.changelogValidationErrors.length && this.allScholarshipsAreValid;
    }

    return this.allScholarshipsAreValid;
  }

  handleConfirmScholarships = () => {
    this.hasSubmitted = true;
    if (this.args.isAdminView) {
      this.args.onUpdateScholarships?.(this.editableScholarships, this.changelog);
    } else {
      this.args.onUpdateScholarships?.(this.editableScholarships);
    }
    this.isEditing = false;
  };

  cancelEdits = () => {
    this.isEditing = false;
    this.setOriginalValuesFromArgs();
  };

  setOriginalValuesFromArgs() {
    const scholarships: ReceivedScholarship[] =
      this.args.application?.fields?.['SCHOLARSHIPS_RECEIVED']?.values || [];
    this.editableScholarships = scholarships.map((scholarship) => {
      return { ...scholarship };
    });
  }

  <template>
    {{#if this.scholarshipCardVisible}}
      <CardsGeneric>
        <:headerTitle>
          {{t "tuition_assistance.program_details.courses.scholarships_added"}}
        </:headerTitle>
        <:body>
          <div class="flex flex-col justify-between">
            <table class="table-fixed mb-2">
              <thead>
                <tr>
                  <th class="font-semibold w-1/2 p-0 pb-1 text-sm text-left">
                    {{t "tuition_assistance.program_details.courses.scholarship_name"}}
                  </th>
                  <th class="font-semibold w-1/2 p-0 pb-1 text-sm text-right">
                    {{t "tuition_assistance.program_details.courses.scholarship_amount"}}
                  </th>
                </tr>
              </thead>
              <tbody>
                {{#each @application.scholarships as |row|}}
                  <tr class="text-sm">
                    <td>{{row.scholarshipName}}</td>
                    <td class="text-right">
                      {{safeFormatNumber
                        (divide row.scholarshipAmount 100)
                        style="currency"
                        currency="USD"
                      }}
                    </td>
                  </tr>
                {{else}}
                  <tr class="text-sm text-center">
                    <td colspan="2" class="py-4">{{t "common.none"}}</td>
                  </tr>
                {{/each}}
              </tbody>
            </table>
          </div>
        </:body>
        <:footer>
          {{#if @isEditable}}
            <div class="flex justify-center items-center my-2">
              <Button @intent="primary" class="w-72" {{on "click" (fn (mut this.isEditing) true)}}>
                {{t "tuition_assistance.program_details.courses.edit_scholarships"}}
              </Button>
            </div>
          {{/if}}
        </:footer>
      </CardsGeneric>
    {{/if}}

    {{#if this.isEditing}}
      <Modal
        @isOpen={{true}}
        @onClose={{this.cancelEdits}}
        class="min-h-64"
        @ariaLabel={{t "common.aria_labels.edit_scholarships"}}
      >
        <:header>{{t "tuition_assistance.program_details.courses.edit_scholarships"}}</:header>
        <:body>
          <EditScholarships
            @application={{@application}}
            @scholarships={{this.editableScholarships}}
            @onUpdate={{fn (mut this.editableScholarships)}}
          />
          {{#if @isAdminView}}
            <div class="w-full w-64">
              <label class="flex font-semibold text-sm text-tio-gray-700 block mb-1">
                {{t "comment"}}
                <span class="text-error-400 ml-1">*</span>
              </label>
              <Textarea
                data-legacy-input
                @value={{this.changelog}}
                @onInput={{fn (mut this.changelog)}}
                @errors={{this.changelogValidationErrors}}
                @size="sm"
                class="changelog-editable__changelog-field"
              />
            </div>
          {{/if}}
        </:body>
        <:footer>
          <Footer
            @isRunning={{false}}
            @onSubmit={{this.handleConfirmScholarships}}
            @submitDisabled={{not this.isFormValid}}
            @onCancel={{this.cancelEdits}}
          />
        </:footer>
      </Modal>
    {{/if}}
  </template>
}
